import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { DesignatedUnitContributionDetailsFormGroup } from '../../../../../employers/add-employer/forms/designated-unit-contribution-details-form-group';
import { DropDownItem } from '../../../../../../models/shared/stylesheet/drop-down-item';
import { Subscribable } from '../../../../../../models/base/subscribable';
import { map, startWith } from 'rxjs/operators';
import { ContributionRateCalculationType } from '../../../../../../models/employers/enum/contribution-rate-calculation-type';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { minimumPercentageRangeValidator } from '../../../../../../validators/minimum-percentage-range.validator';
import { Validators } from '@angular/forms';
import { contributionRatePercentageValidator } from '../../../../../../validators/contribution-rate-percentage.validator';

@Component({
  selector: 'app-create-designated-unit-required-contributions-form',
  templateUrl: './designated-unit-required-contributions-form.component.html',
  styleUrls: ['./designated-unit-required-contributions-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateDesignatedUnitRequiredContributionsFormComponent extends Subscribable implements OnInit {
  @Input() formGroup!: DesignatedUnitContributionDetailsFormGroup;
  @Input() formSubmitted: boolean = false;
  @Input() basisForContributionRatesDropdownItems!: DropDownItem[];
  @Input() rateCalculationDropdownItems!: DropDownItem[];

  public showMatchedPercentage$ = new BehaviorSubject(false);
  public showMatchedPercentageRange$ = new BehaviorSubject(false);
  public matchedIsYMPE$ = new BehaviorSubject(false);
  public showMatchedPercentageInputs$ = combineLatest([
    this.showMatchedPercentage$,
    this.showMatchedPercentageRange$
  ]).pipe(map(([showPercentage, showRange]) => showPercentage || showRange));

  public matchedFirstPercentageLabel$ = this.matchedIsYMPE$.pipe(
    map(isYMPE => (isYMPE ? $localize`Percentage <= YMPE` : $localize`Minimum Percentage`))
  );
  public matchedSecondPercentageLabel$ = this.matchedIsYMPE$.pipe(
    map(isYMPE => (isYMPE ? $localize`Percentage > YMPE` : $localize`Maximum Percentage`))
  );

  public showUnmatchedPercentage$ = new BehaviorSubject(false);
  public showUnmatchedPercentageRange$ = new BehaviorSubject(false);
  public unmatchedIsYMPE$ = new BehaviorSubject(false);
  public showUnmatchedPercentageInputs$ = combineLatest([
    this.showUnmatchedPercentage$,
    this.showUnmatchedPercentageRange$
  ]).pipe(map(([showPercentage, showRange]) => showPercentage || showRange));

  public unmatchedFirstPercentageLabel$ = this.unmatchedIsYMPE$.pipe(
    map(isYMPE => (isYMPE ? $localize`Percentage <= YMPE` : $localize`Minimum Percentage`))
  );
  public unmatchedSecondPercentageLabel$ = this.unmatchedIsYMPE$.pipe(
    map(isYMPE => (isYMPE ? $localize`Percentage > YMPE` : $localize`Maximum Percentage`))
  );

  private listenToMatchedShowRange$ = this.showMatchedPercentageRange$.subscribeWhileAlive({
    owner: this,
    next: showRange => {
      if (this.formGroup?.disabled) return;
      if (showRange) {
        this.formGroup?.controls?.matchedContributionRatePercentage?.disable({ emitEvent: false });
        this.formGroup?.controls?.matchedContributionRateRangeFirstPercentage?.enable({ emitEvent: false });
        this.formGroup?.controls?.matchedContributionRateRangeSecondPercentage?.enable({ emitEvent: false });
      } else {
        this.formGroup?.controls?.matchedContributionRatePercentage?.enable({ emitEvent: false });
        this.formGroup?.controls?.matchedContributionRateRangeFirstPercentage?.disable({ emitEvent: false });
        this.formGroup?.controls?.matchedContributionRateRangeSecondPercentage?.disable({ emitEvent: false });
      }
    }
  });

  private listenToUnmatchedShowRange$ = this.showUnmatchedPercentageRange$.subscribeWhileAlive({
    owner: this,
    next: showRange => {
      if (this.formGroup?.disabled) return;
      if (showRange) {
        this.formGroup?.controls?.unmatchedContributionRatePercentage?.disable({ emitEvent: false });
        this.formGroup?.controls?.unmatchedContributionRateRangeFirstPercentage?.enable({ emitEvent: false });
        this.formGroup?.controls?.unmatchedContributionRateRangeSecondPercentage?.enable({ emitEvent: false });
      } else {
        this.formGroup?.controls?.unmatchedContributionRatePercentage?.enable({ emitEvent: false });
        this.formGroup?.controls?.unmatchedContributionRateRangeFirstPercentage?.disable({ emitEvent: false });
        this.formGroup?.controls?.unmatchedContributionRateRangeSecondPercentage?.disable({ emitEvent: false });
      }
    }
  });

  private listenToMatchedYMPE$ = this.matchedIsYMPE$.subscribeWhileAlive({
    owner: this,
    next: isYMPE => {
      const control = this.formGroup?.controls?.matchedContributionRateRangeFirstPercentage;

      if (isYMPE) {
        // Remove the minimumPercentageRangeValidator
        const validators = control?.validator
          ? [
              Validators.required,
              contributionRatePercentageValidator(
                'matchedContributionRateRangeFirstPercentage',
                'unmatchedContributionRateRangeFirstPercentage'
              )
            ]
          : [];
        control?.setValidators(validators);
      } else {
        // Add the minimumPercentageRangeValidator
        control?.setValidators([
          Validators.required,
          contributionRatePercentageValidator(
            'matchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeFirstPercentage'
          ),
          minimumPercentageRangeValidator(
            'matchedContributionRateRangeFirstPercentage',
            'matchedContributionRateRangeSecondPercentage'
          )
        ]);
      }
      control?.updateValueAndValidity({ emitEvent: false });
    }
  });

  private listenToUnmatchedYMPE$ = this.unmatchedIsYMPE$.subscribeWhileAlive({
    owner: this,
    next: isYMPE => {
      const control = this.formGroup?.controls?.unmatchedContributionRateRangeFirstPercentage;

      if (isYMPE) {
        // Remove the minimumPercentageRangeValidator
        const validators = control?.validator
          ? [
              Validators.required,
              contributionRatePercentageValidator(
                'matchedContributionRateRangeFirstPercentage',
                'unmatchedContributionRateRangeFirstPercentage'
              )
            ]
          : [];
        control?.setValidators(validators);
      } else {
        // Add the minimumPercentageRangeValidator
        control?.setValidators([
          Validators.required,
          contributionRatePercentageValidator(
            'matchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeFirstPercentage'
          ),
          minimumPercentageRangeValidator(
            'unmatchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeSecondPercentage'
          )
        ]);
      }
      control?.updateValueAndValidity({ emitEvent: false });
    }
  });

  public customErrorMessages = {
    contributionRateBasisTypeId: {
      required: $localize`Basis for Contribution Rates required`
    },
    matchedContributionRateTypeId: {
      required: $localize`Matched Contribution Rate Calculation required`,
      bothNotApplicable: $localize`Matched and unmatched contribution rate calculations cannot both be N/A`
    },
    unmatchedContributionRateTypeId: {
      bothNotApplicable: $localize`Matched and unmatched contribution rate calculations cannot both be N/A`
    },
    matchedContributionRatePercentage: {
      required: $localize`Percentage required`,
      max: $localize`Percentage cannot be more than 9.00%`,
      min: $localize`Percentage must be at least 1.00%`
    },
    matchedContributionRateRangeFirstPercentage: {
      required: $localize`Minimum percentage required`,
      invalidMinimumRatePercentage: $localize`The minimum required contribution rate must be equal to or greater than 1%`,
      invalidMinimumPercentage: $localize`Invalid percentage range`,
      max: $localize`Percentage cannot be more than 9.00%`,
      min: $localize`Percentage must be at least 1.00%`
    },
    matchedContributionRateRangeSecondPercentage: {
      required: $localize`Maximum percentage required`,
      max: $localize`Percentage cannot be more than 9.00%`,
      min: $localize`Percentage must be at least 1.00%`
    },
    unmatchedContributionRatePercentage: {
      required: $localize`Percentage required`,
      max: $localize`Percentage cannot be more than 9.00%`,
      min: $localize`Percentage must be at least 1.00%`
    },
    unmatchedContributionRateRangeFirstPercentage: {
      required: $localize`Minimum percentage required`,
      invalidMinimumRatePercentage: $localize`The minimum required contribution rate must be equal to or greater than 1%`,
      invalidMinimumPercentage: $localize`Invalid percentage range`,
      max: $localize`Percentage cannot be more than 9.00%`,
      min: $localize`Percentage must be at least 1.00%`
    },
    unmatchedContributionRateRangeSecondPercentage: {
      required: $localize`Maximum percentage required`,
      max: $localize`Percentage cannot be more than 9.00%`,
      min: $localize`Percentage must be at least 1.00%`
    }
  };

  public ngOnInit() {
    this.listenToForms();
    this.setMatchedControlsState(this.formGroup.controls.matchedContributionRateTypeId.value);
    this.setUnmatchedControlsState(this.formGroup.controls.unmatchedContributionRateTypeId.value);
  }

  private setMatchedControlsState(typeId: number | null) {
    if (this.formGroup.disabled) return;
    const controls = this.formGroup.controls;
    if (typeId === ContributionRateCalculationType.Percent) {
      controls.matchedContributionRatePercentage.enable({ emitEvent: false });
      controls.matchedContributionRateRangeFirstPercentage.disable({ emitEvent: false });
      controls.matchedContributionRateRangeSecondPercentage.disable({ emitEvent: false });
    } else if (
      typeId === ContributionRateCalculationType.PercentRange ||
      typeId === ContributionRateCalculationType.YMPEIntegratedPercent
    ) {
      controls.matchedContributionRatePercentage.disable({ emitEvent: false });
      controls.matchedContributionRateRangeFirstPercentage.enable({ emitEvent: false });
      controls.matchedContributionRateRangeSecondPercentage.enable({ emitEvent: false });
    } else {
      controls.matchedContributionRatePercentage.disable({ emitEvent: false });
      controls.matchedContributionRateRangeFirstPercentage.disable({ emitEvent: false });
      controls.matchedContributionRateRangeSecondPercentage.disable({ emitEvent: false });
    }
  }

  private setUnmatchedControlsState(typeId: number | null) {
    if (this.formGroup.disabled) return;
    const controls = this.formGroup.controls;
    if (typeId === ContributionRateCalculationType.Percent) {
      controls.unmatchedContributionRatePercentage.enable({ emitEvent: false });
      controls.unmatchedContributionRateRangeFirstPercentage.disable({ emitEvent: false });
      controls.unmatchedContributionRateRangeSecondPercentage.disable({ emitEvent: false });
    } else if (
      typeId === ContributionRateCalculationType.PercentRange ||
      typeId === ContributionRateCalculationType.YMPEIntegratedPercent
    ) {
      controls.unmatchedContributionRatePercentage.disable({ emitEvent: false });
      controls.unmatchedContributionRateRangeFirstPercentage.enable({ emitEvent: false });
      controls.unmatchedContributionRateRangeSecondPercentage.enable({ emitEvent: false });
    } else {
      controls.unmatchedContributionRatePercentage.disable({ emitEvent: false });
      controls.unmatchedContributionRateRangeFirstPercentage.disable({ emitEvent: false });
      controls.unmatchedContributionRateRangeSecondPercentage.disable({ emitEvent: false });
    }
  }

  private listenToForms(): void {
    this.formGroup.controls.matchedContributionRateTypeId.valueChanges
      .pipe(startWith(this.formGroup?.controls?.matchedContributionRateTypeId?.value))
      .subscribeWhileAlive({
        owner: this,
        next: typeId => {
          this.setMatchedControlsState(typeId);
          this.showMatchedPercentage$.next(typeId === ContributionRateCalculationType.Percent);
          this.showMatchedPercentageRange$.next(
            typeId === ContributionRateCalculationType.PercentRange ||
              typeId === ContributionRateCalculationType.YMPEIntegratedPercent
          );
          this.matchedIsYMPE$.next(typeId === ContributionRateCalculationType.YMPEIntegratedPercent);
          if (typeId === ContributionRateCalculationType.NA) {
            this.setMatchedControlsState(typeId);
          }
          this.formGroup.controls.unmatchedContributionRateTypeId.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false
          });
        }
      });

    this.formGroup.controls.unmatchedContributionRateTypeId.valueChanges
      .pipe(startWith(this.formGroup?.controls?.unmatchedContributionRateTypeId?.value))
      .subscribeWhileAlive({
        owner: this,
        next: typeId => {
          this.setUnmatchedControlsState(typeId);
          this.showUnmatchedPercentage$.next(typeId === ContributionRateCalculationType.Percent);
          this.showUnmatchedPercentageRange$.next(
            typeId === ContributionRateCalculationType.PercentRange ||
              typeId === ContributionRateCalculationType.YMPEIntegratedPercent
          );
          this.unmatchedIsYMPE$.next(typeId === ContributionRateCalculationType.YMPEIntegratedPercent);
          if (typeId === ContributionRateCalculationType.NA || typeId === null) {
            this.setUnmatchedControlsState(typeId);
          }
          this.formGroup.controls.matchedContributionRateTypeId.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false
          });
        }
      });

    this.formGroup.controls.matchedContributionRateRangeFirstPercentage.valueChanges.subscribeWhileAlive({
      owner: this,
      next: _ => {
        this.formGroup.controls.unmatchedContributionRateRangeFirstPercentage.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false
        });
      }
    });

    this.formGroup.controls.unmatchedContributionRateRangeFirstPercentage.valueChanges.subscribeWhileAlive({
      owner: this,
      next: _ => {
        this.formGroup.controls.matchedContributionRateRangeFirstPercentage.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false
        });
      }
    });

    this.formGroup.controls.matchedContributionRateRangeSecondPercentage.valueChanges.subscribeWhileAlive({
      owner: this,
      next: _ => {
        this.formGroup.controls.matchedContributionRateRangeFirstPercentage.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false
        });
      }
    });

    this.formGroup.controls.unmatchedContributionRateRangeSecondPercentage.valueChanges.subscribeWhileAlive({
      owner: this,
      next: _ => {
        this.formGroup.controls.unmatchedContributionRateRangeFirstPercentage.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false
        });
      }
    });
  }
}
